import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigation } from "../../hooks/useNavigation";
import Button from "../Button";

// Hook for detecting reduced motion preference
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

interface AnimatedHeroProps {
  title: React.ReactNode;
  rotatingWords?: string[];
  subtitle?: string;
  description: string;
  primaryCta: { text: string; href: string };
  secondaryCta: { text: string; href: string };
  images?: string[];
  imageAlts?: string[]; // Optional descriptive alt texts for images
}

function AnimatedHero({
  title,
  rotatingWords = [],
  subtitle,
  description,
  primaryCta,
  secondaryCta,
  images = [],
  imageAlts = []
}: AnimatedHeroProps) {
  const navigate = useNavigation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [titleNumber, setTitleNumber] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const titles = useMemo(() => rotatingWords, [rotatingWords]);

  // Rotating words effect
  useEffect(() => {
    if (titles.length === 0) return;
    const timeoutId = setTimeout(() => {
      if (titleNumber === titles.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles.length]);

  // Image carousel effect
  useEffect(() => {
    if (images.length <= 1) return;
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [images.length]);

  return (
    <section className="bg-white py-12 sm:py-16 lg:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-x-16 lg:gap-y-6 items-start">

          {/* Text Content Wrapper: Contents on Mobile (for grid order), Flex on Desktop (for spacing) */}
          <div className="contents lg:flex lg:flex-col lg:gap-6 lg:col-start-1 lg:row-start-1 lg:pt-12">

            {/* 1. Header (Tagline + Title) */}
            <div className="order-1 flex flex-col gap-4 lg:gap-6 pt-2 lg:pt-0">
              {/* Desktop Tagline (Hidden on Mobile) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="hidden lg:inline-flex items-center space-x-2"
              >
                <span className="h-px w-8 bg-amber-500"></span>
                <span className="text-amber-600 font-medium tracking-wide uppercase text-sm sm:text-base">
                  {description}
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.1]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {title}
              </motion.h1>
            </div>

            {/* 3. Content (Subtitle + Buttons) */}
            <div className="order-3 flex flex-col gap-6">
              {/* Mobile Tagline (Hidden on Desktop) - Appears below image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="lg:hidden inline-flex items-center space-x-2 -mt-2"
              >
                <span className="h-px w-8 bg-amber-500"></span>
                <span className="text-amber-600 font-medium tracking-wide uppercase text-sm sm:text-base">
                  {description}
                </span>
              </motion.div>

              <motion.p
                className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.2 }}
              >
                {subtitle}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.3 }}
              >
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto min-w-[200px] shadow-xl hover:shadow-2xl transition-all duration-300"
                  onClick={() => navigate(primaryCta.href)}
                >
                  {primaryCta.text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto min-w-[200px] flex items-center justify-center gap-2 hover:bg-slate-50/50"
                  onClick={() => navigate(secondaryCta.href)}
                >
                  {secondaryCta.text}
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0 }}
                animate={prefersReducedMotion ? {} : { opacity: 1 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.4 }}
                className="flex flex-col gap-3 pt-4"
              >
                <div className="flex items-center gap-6 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-500" />
                    <span>Eidg. Fachausweis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-500" />
                    <span>12+ Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-500" />
                    <span>16K LinkedIn</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 max-w-md">
                  OCPD-Expertise = Systematische Präzision als Methode. Strukturiert, wiederholbar, fehlerfrei.
                </p>
              </motion.div>
            </div>
          </div>

          {/* 2. Image - Middle on Mobile, Right Column on Desktop */}
          <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 relative w-full">
            <motion.div
              className="relative aspect-square lg:aspect-[4/5] max-h-[400px] lg:max-h-[700px] w-full mx-auto"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <div
                className="relative h-full w-full overflow-hidden shadow-none border-none ring-0"
                role="region"
                aria-label="Hero image carousel"
                aria-live="polite"
              >
                {images.map((img, index) => (
                  <motion.div
                    key={index}
                    className="absolute inset-0 w-full h-full"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: index === currentImageIndex ? 1 : 0,
                      zIndex: index === currentImageIndex ? 10 : 0
                    }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                  >
                    <img
                      src={img}
                      alt={imageAlts[index] || `Professional consulting image ${index + 1}`}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      className="w-full h-full object-cover object-top"
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}

export { AnimatedHero };

